import {
  DeliveredProcedureOutlined,
  DropboxOutlined,
  FileProtectOutlined,
  FundProjectionScreenOutlined,
  LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined,  UserOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import {Button, Divider, Layout, Menu, theme} from 'antd';
import {jwtDecode} from 'jwt-decode';
import React, {useEffect, useRef, useState} from 'react';
import {MdOutlineLocalOffer} from 'react-icons/md';
import {useDispatch, useSelector} from 'react-redux';
import {NavLink, Route, Routes, useLocation, useNavigate} from 'react-router-dom';

import r from '../../assets/Images/Logo.png';
import petal from '../../assets/Images/RPLogo1.png';
import {RQGetProfileAPI} from '../../reactQuery/apis';
import {logoutStart} from '../../redux/authAction';
import {selectAuth} from '../../redux/selectors';

import {DeliveryCharges} from './DeliveryCharges';
import {DrawerMenu} from './DrawerMenu';
import {MainDashboard} from './MainDashboard';
import Offers from './Offers/Offers';
import {Order} from './Order';
import {Product} from './Product';
import {ProductDetailScreen} from './Product/ProductDetailScreen';
import ProductEditScreen from './Product/ProductEditScreen/ProductEditScreen';
import {ProfileScreen} from './ProfileScreen';
import {SingleUser} from './SingleUser';
import {EditProfile} from './SingleUser/EditProfile';
import {OrderSummary} from './SingleUser/OrderSummary';
import {UserHistory} from './SingleUser/UserHistory';
import {UserWishlist} from './SingleUser/UserWishlits';
import {UserDetails} from './UserDetails';

const {Header, Content,  Sider} = Layout;

function getItem(label, key, icon, path, children) {
  return {
    key,
    icon,
    label,
    path,
    children,
  };
}

const AdminDashboard = () => {
  const [collapsed, setCollapsed] = useState(false);

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const location = useLocation();

  const auth = useSelector(selectAuth);

  const token = auth?.tokenData;

  const decodedToken = token ? jwtDecode(token) : {};

  const {data: userProfileDetails} = RQGetProfileAPI(decodedToken?.userId, token);

  const items = [
    getItem('Dashboard', '1', <FundProjectionScreenOutlined />, '/dashboard'),
    getItem('Users', '2', <UsergroupAddOutlined />, '/user'),
    getItem('Orders', '4', <FileProtectOutlined />, '/order'),
    getItem('Delivery Charges', '4', <DeliveredProcedureOutlined />, '/delivery'),
    getItem('Offers', '4', <MdOutlineLocalOffer />, '/offers'),
    getItem('Product', 'sub1', <DropboxOutlined />, '/team/product', [
      getItem('Product', '3', null, '/team/product'),
    ]),
    getItem('Profile', '5', <UserOutlined />, '/profile'),
  ];
  const skipRedirectRef = useRef(false);

  useEffect(() => {
    if (!auth.loggedIn) {
      if (skipRedirectRef.current) {
        skipRedirectRef.current = false;
      } else {
        navigate('/auth/login');
      }
    }
  }, [auth.loggedIn]);

  const {
    token: {colorBgContainer, borderRadiusLG},
  } = theme.useToken();

  const handleLogout = () => {
    skipRedirectRef.current = true;
    dispatch(logoutStart());
    navigate('/');
  };

  return (
    <Layout className={'min-h-screen'}>
      <Sider
        trigger={null}
        className={'h-screen overflow-y-auto lg:block hidden sticky top-0 left-0  bg-white text-black  shadow-right shadow'}

        collapsible collapsed={collapsed}>
        <div className={'demo-logo-vertical'} />
        <div className={'flex items-center justify-center h-24 text-2xl my-2'}>
          {collapsed ? (
            <NavLink to={'/'}>
              <img src={petal} alt={'Logo Expanded'} className={'h-10'} />
            </NavLink>
          ) : (
            <NavLink to={'/'}>
              <img src={r} alt={'Logo Collapsed'}className={'h-24'}  />
            </NavLink>
          )}
        </div>
        <Divider className={'my-2'} />
   
        <Menu mode={'inline'} className={'custom-menu'} selectedKeys={[location.pathname]}>
          {items.map((item, index) => (
            <React.Fragment key={index}>
              <Menu.Item
                key={item.path}
                icon={item.icon}
                onClick={() => {
                  navigate(item.path);
                }}
              >
                {item.label}
              </Menu.Item>
              {index !== items.length - 1 && (
                <Divider className={'my-2 border-gray-300'} />
              )}
            </React.Fragment>
          ))}
        </Menu>
      </Sider>

      <Layout>
        <Header
          className={'sticky top-0 z-50 flex items-center justify-between px-4  shadow-inner'}
          style={{background: colorBgContainer, height: 64}}
        >
          {/* Sidebar toggle button for desktop */}
          <Button
            type={'text'}
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            className={'text-xl lg:block hidden'}
          />

          {/* Drawer menu for mobile */}
          <div className={'lg:hidden block'}>
            <DrawerMenu items={items} />
          </div>

          {/* Welcome text */}
          <div className={'flex items-center gap-3'}>
            <h1 className={'text-lg '}>
      Welcome,&nbsp;
              <span className={'text-primary-900'}>{userProfileDetails?.firstName}</span>
              <span className={'text-xs text-gray-500'}> ({decodedToken.role})</span>
            </h1>

            {/* Logout button */}
            <Button
              onClick={handleLogout}
              type={'primary'}
              className={'flex items-center gap-1'}
            >
              <LogoutOutlined />
            </Button>
          </div>
        </Header>

        <Content
          style={{
            margin: '24px 16px',
            padding: 24,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <Routes>
            <Route path={'/dashboard'} element={<MainDashboard />} />
            <Route path={'/user'} element={<UserDetails />} />
            <Route path={'/profile'} element={<ProfileScreen />} />
            <Route path={'/team/product'} element={<Product />} />
            <Route path={'/productDetail'} element={<ProductDetailScreen />} />
            <Route path={'/productEdit'} element={<ProductEditScreen />} />
            <Route path={'/order'} element={<Order />} />
            <Route path={'/offers'} element={<Offers />} />
            <Route path={'/delivery'} element={<DeliveryCharges />} />
            <Route path={'/user/:id'} element={<SingleUser />} />
            <Route path={'/edituser'} element={<EditProfile />} />
            <Route path={'/uhistory/:id'} element={<UserHistory />} />
            <Route path={'/order/:id'} element={<OrderSummary />} />
            <Route path={'/wishlist'} element={<UserWishlist />} />
          </Routes>
        </Content>     
      </Layout>
    </Layout>
  );
};

export default AdminDashboard;
